@if (view(); as v) {
  <p-confirmdialog />

  <h3 class="mb-0">{{ 'measurement.datapoint.title' | translate }}</h3>
  <p class="mb-3 text-surface-500 dark:text-surface-400">
    {{ v.sourceLabel }} · {{ v.timestamp | date:'dd/MM/yyyy HH:mm:ss' }}
  </p>

  <!-- Prev / position / next -->
  <div class="flex items-center justify-between mb-2">
    <p-button
      icon="fa-solid fa-chevron-left"
      [label]="'measurement.datapoint.prev' | translate"
      [outlined]="true"
      size="small"
      [disabled]="!v.nav.hasPrev"
      (onClick)="prev()"
    />
    <span class="text-sm text-surface-500 dark:text-surface-400 tabular-nums">
      {{ v.nav.index + 1 }} / {{ v.nav.total }}
    </span>
    <p-button
      icon="fa-solid fa-chevron-right"
      iconPos="right"
      [label]="'measurement.datapoint.next' | translate"
      [outlined]="true"
      size="small"
      [disabled]="!v.nav.hasNext"
      (onClick)="next()"
    />
  </div>

  <p-divider></p-divider>

  <div class="flex flex-col gap-4">

    <!-- Values -->
    <p-card appearance="outlined">
      <ng-template #title>{{ 'measurement.datapoint.values' | translate }}</ng-template>
      <div class="grid gap-4" style="grid-template-columns: repeat(auto-fit, minmax(160px, 1fr))">
        @for (val of v.values; track val.key) {
          <div class="flex flex-col gap-0.5">
            <span
              class="text-sm font-medium"
              [class.text-primary]="val.key === v.highlightKey"
              [class.text-surface-500]="val.key !== v.highlightKey"
              [class.dark:text-surface-400]="val.key !== v.highlightKey"
            >
              {{ val.label }}{{ val.unit ? ' · ' + val.unit : '' }}
            </span>
            <span class="text-base text-surface-900 dark:text-surface-0 tabular-nums break-words">
              @if (val.value === null) { — }
              @else if (isNumber(val.value)) { {{ val.value | number:'1.0-4' }} }
              @else { {{ val.value }} }
            </span>
          </div>
        }
      </div>
    </p-card>

    <!-- Comparison -->
    @if (hasComparison()) {
      <p-card appearance="outlined">
        <ng-template #title>{{ 'measurement.datapoint.compare' | translate }}</ng-template>
        <p-table [value]="compareRows()" styleClass="p-datatable-sm" [scrollable]="false">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'measurement.datapoint.variable' | translate }}</th>
              <th class="text-right">{{ 'measurement.datapoint.vsPrev' | translate }}</th>
              <th class="text-right">{{ 'measurement.datapoint.current' | translate }}</th>
              <th class="text-right">{{ 'measurement.datapoint.vsNext' | translate }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [class.font-semibold]="row.key === v.highlightKey">
              <td>{{ row.label }}</td>
              <td class="text-right tabular-nums">
                @if (row.prev) {
                  <div>{{ row.prev.value | number:'1.0-4' }}</div>
                  <div
                    class="text-xs"
                    [class.text-green-600]="row.prev.delta > 0"
                    [class.dark:text-green-400]="row.prev.delta > 0"
                    [class.text-red-500]="row.prev.delta < 0"
                    [class.dark:text-red-400]="row.prev.delta < 0"
                    [class.text-surface-400]="row.prev.delta === 0"
                  >
                    {{ formatDelta(row.prev.delta, row.prev.pct) }}
                  </div>
                } @else { <span class="text-surface-400">—</span> }
              </td>
              <td class="text-right tabular-nums text-surface-900 dark:text-surface-0">
                {{ row.current | number:'1.0-4' }}
              </td>
              <td class="text-right tabular-nums">
                @if (row.next) {
                  <div>{{ row.next.value | number:'1.0-4' }}</div>
                  <div
                    class="text-xs"
                    [class.text-green-600]="row.next.delta > 0"
                    [class.dark:text-green-400]="row.next.delta > 0"
                    [class.text-red-500]="row.next.delta < 0"
                    [class.dark:text-red-400]="row.next.delta < 0"
                    [class.text-surface-400]="row.next.delta === 0"
                  >
                    {{ formatDelta(row.next.delta, row.next.pct) }}
                  </div>
                } @else { <span class="text-surface-400">—</span> }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    }

    <!-- Source file -->
    @if (v.attachments.length) {
      <p-card appearance="outlined">
        <ng-template #title>{{ 'measurement.datapoint.sourceFile' | translate }}</ng-template>
        <div class="flex flex-col gap-3">
          @for (att of v.attachments; track att.ingestedCsvId) {
            <div class="border border-surface-200 dark:border-surface-700 rounded-border p-4">
              <div class="flex items-start gap-4">
                <div
                  class="flex items-center justify-center w-12 h-12 rounded-border shrink-0"
                  style="background: var(--color-shm-bg)"
                >
                  <i class="fa-solid fa-file-csv text-xl" style="color: var(--color-shm)"></i>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="font-medium break-all">
                    {{ att.filename || ('measurement.datapoint.sourceFileUnnamed' | translate) }}
                  </div>
                  @if (att.status) {
                    <div class="mt-1">
                      <p-tag
                        [severity]="csvStatusSeverity(att.status)"
                        [value]="('measurement.rawDataViewer.status.' + att.status) | translate"
                      />
                    </div>
                  }
                </div>
                @if (downloadCsv()) {
                  <p-button
                    [label]="'button.download' | translate"
                    icon="fa-solid fa-download"
                    severity="secondary"
                    [outlined]="true"
                    (onClick)="onDownloadCsv(att)"
                  />
                }
              </div>
            </div>
          }
        </div>
      </p-card>
    }

    <!-- Danger zone -->
    @if (adapter().canDelete()) {
      <p-card appearance="outlined" styleClass="border-red-200 dark:border-red-800">
        <ng-template #title>
          <span class="text-red-600 dark:text-red-400">
            {{ 'measurement.datapoint.remove.title' | translate }}
          </span>
        </ng-template>
        <div class="flex flex-col gap-4">
          <p class="text-sm text-surface-600 dark:text-surface-400 m-0">
            {{ 'measurement.datapoint.remove.body' | translate:{ count: adapter().affectedCount?.() ?? 0 } }}
          </p>
          <div class="flex justify-end">
            <p-button
              [label]="'measurement.datapoint.remove.button' | translate"
              icon="fa-solid fa-trash"
              severity="danger"
              [loading]="deleting()"
              (onClick)="onDeleteClick($event)"
            />
          </div>
        </div>
      </p-card>
    }
  </div>
}
